import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { Container, AppBar, Toolbar, Typography, Button, Menu, MenuItem } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import LandingPage from './LandingPage';
import ExperimentConfig from './ExperimentConfig';
import { BuilderProvider } from './builder/BuilderContext';
import ExperimentBuilder from './builder/ExperimentBuilder';
import Experiment from './Experiment';
import Agents from './Agents';
import RunningExperiments from './RunningExperiments';
import ExperimentDetails from './ExperimentDetails';
import Profile from './Profile';
import Expressions from './Operators';
import IdentityBuilder from './IdentityBuilder';
import SimulationJobs from './SimulationJobs';
import Leaderboards from './Leaderboards';

// Protected route component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth0();
    if (isLoading) return <div>Loading...</div>;
    return isAuthenticated ? children : <Navigate to="/" />;
};

function AppShell() {
    const { loginWithRedirect, logout, user, isAuthenticated } = useAuth0();
    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const [anchorElExperiments, setAnchorElExperiments] = React.useState(null);
    const [anchorElSimulations, setAnchorElSimulations] = React.useState(null);
    const [anchorElAgents, setAnchorElAgents] = React.useState(null);
    const location = useLocation();

    // Hide navbar on active participant experiment route: /experiments/:exptID
    // Show on /experiments/:exptID/details and other routes
    const hideNav = React.useMemo(() => {
        const p = location.pathname;
        // Match /experiments/<id> exactly (no trailing /details or /something)
        const expMatch = /^\/experiments\/[^/]+$/.test(p);
        return expMatch; // hide when participant running experiment
    }, [location.pathname]);

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleOpenExperimentsMenu = (event) => {
        setAnchorElExperiments(event.currentTarget);
    };

    const handleCloseExperimentsMenu = () => {
        setAnchorElExperiments(null);
    };

    const handleOpenSimulationsMenu = (event) => {
        setAnchorElSimulations(event.currentTarget);
    };

    const handleCloseSimulationsMenu = () => {
        setAnchorElSimulations(null);
    };

    const handleOpenAgentsMenu = (event) => {
        setAnchorElAgents(event.currentTarget);
    };

    const handleCloseAgentsMenu = () => {
        setAnchorElAgents(null);
    };

    return (
        <div data-testid="app-container">
            {!hideNav && (
                <AppBar position="static" color="primary">
                        <Toolbar>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                HAX Platform
                            </Typography>
                            <Button color="inherit" component={Link} to="/">
                                Home
                            </Button>
                            {isAuthenticated && (
                                <>
                                    {/* Experiments menu */}
                                    <Button color="inherit" onClick={handleOpenExperimentsMenu}>
                                        Experiments
                                    </Button>
                                    <Menu
                                        anchorEl={anchorElExperiments}
                                        open={Boolean(anchorElExperiments)}
                                        onClose={handleCloseExperimentsMenu}
                                        keepMounted
                                    >
                                        <MenuItem component={Link} to="/experiments" onClick={handleCloseExperimentsMenu}>
                                            All Experiments
                                        </MenuItem>
                                        <MenuItem component={Link} to="/builder" onClick={handleCloseExperimentsMenu}>
                                            New Experiment (Builder)
                                        </MenuItem>
                                        <MenuItem component={Link} to="/experiments/new" onClick={handleCloseExperimentsMenu}>
                                            Legacy Config
                                        </MenuItem>
                                    </Menu>

                                    {/* Agents menu */}
                                    <Button color="inherit" onClick={handleOpenAgentsMenu}>
                                        Agents
                                    </Button>
                                    <Menu
                                        anchorEl={anchorElAgents}
                                        open={Boolean(anchorElAgents)}
                                        onClose={handleCloseAgentsMenu}
                                        keepMounted
                                    >
                                        <MenuItem component={Link} to="/agents/leaderboards" onClick={handleCloseAgentsMenu}>
                                            Leaderboards
                                        </MenuItem>
                                        <MenuItem component={Link} to="/agents" onClick={handleCloseAgentsMenu}>
                                            All Agents
                                        </MenuItem>
                                    </Menu>

                                    {/* Simulations menu */}
                                    <Button color="inherit" onClick={handleOpenSimulationsMenu}>
                                        Simulations
                                    </Button>
                                    <Menu
                                        anchorEl={anchorElSimulations}
                                        open={Boolean(anchorElSimulations)}
                                        onClose={handleCloseSimulationsMenu}
                                        keepMounted
                                    >
                                        <MenuItem component={Link} to="/identity-builder" onClick={handleCloseSimulationsMenu}>
                                            Identity Builder
                                        </MenuItem>
                                        <MenuItem component={Link} to="/expressions" onClick={handleCloseSimulationsMenu}>
                                            Expressions (Legacy)
                                        </MenuItem>
                                        <MenuItem component={Link} to="/simulations" onClick={handleCloseSimulationsMenu}>
                                            Simulation Jobs
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}

                            {!isAuthenticated ? (
                                <Button color="inherit" onClick={() => loginWithRedirect()}>
                                    Login
                                </Button>
                            ) : (
                                <>
                                    <Button color="inherit" onClick={handleOpenUserMenu}>
                                        {user.nickname}
                                    </Button>
                                    <Menu
                                        anchorEl={anchorElUser}
                                        open={Boolean(anchorElUser)}
                                        onClose={handleCloseUserMenu}
                                        keepMounted
                                    >
                                        <MenuItem component={Link} to="/profile" onClick={handleCloseUserMenu}>
                                            Profile
                                        </MenuItem>
                                        <MenuItem onClick={() => {
                                            handleCloseUserMenu();
                                            logout({ returnTo: window.location.origin });
                                        }}>
                                            Logout
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}
                        </Toolbar>
                </AppBar>
            )}

            <Container maxWidth="xl" sx={{ mt: hideNav ? 0 : 4, px: hideNav ? 0 : 2 }}>
                        <Routes>
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/experiments" element={
                                <ProtectedRoute>
                                    <RunningExperiments />
                                </ProtectedRoute>
                            } />
                            <Route path="/experiments/new" element={
                                <ProtectedRoute>
                                    <ExperimentConfig />
                                </ProtectedRoute>
                            } />
                            <Route path="/builder" element={
                                <ProtectedRoute>
                                    <BuilderProvider>
                                        <ExperimentBuilder />
                                    </BuilderProvider>
                                </ProtectedRoute>
                            } />
                            <Route path="/experiments/:exptID/details" element={
                                <ProtectedRoute>
                                    <ExperimentDetails />
                                </ProtectedRoute>
                            } />
                            <Route path="/experiments/:exptID" element={
                                <Experiment />
                            } />
                            <Route path="/agents/leaderboards" element={
                                <ProtectedRoute>
                                    <Leaderboards />
                                </ProtectedRoute>
                            } />
                            <Route path="/agents" element={
                                <ProtectedRoute>
                                    <Agents />
                                </ProtectedRoute>
                            } />
                            <Route path="/identity-builder" element={
                                <ProtectedRoute>
                                    <IdentityBuilder />
                                </ProtectedRoute>
                            } />
                            <Route path="/expressions" element={
                                <ProtectedRoute>
                                    <Expressions />
                                </ProtectedRoute>
                            } />
                            <Route path="/simulations" element={
                                <ProtectedRoute>
                                    <SimulationJobs />
                                </ProtectedRoute>
                            } />
                            <Route path="/profile" element={
                                <ProtectedRoute>
                                    <Profile />
                                </ProtectedRoute>
                            } />
                            <Route path="*" element={<div>404 Page Not Found</div>} />
                        </Routes>
            </Container>
        </div>
    );
}

function App() {
    return (
        <DndProvider backend={HTML5Backend}>
            <Router>
                <AppShell />
            </Router>
        </DndProvider>
    );
}

export default App;
